import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import {
  CheckCircle2,
  Printer,
  Building2,
  CalendarDays,
  Users,
  Bed,
  Utensils,
  CreditCard,
  Gift,
  StickyNote,
  MapPin,
  Phone,
  Mail,
  Globe,
  Tag,
  AlertCircle,
  Timer,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import LoadingItem from "@/components/LoadingItem";
import { ModeToggle } from "@/components/ThemeToggle";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { environment } from "@/config/env";
import { renderMarkdownAndSanitize } from "@/lib/markdown";

interface VariantService {
  id?: number;
  serviceCode?: string;
  description: string;
  amount: number;
  isOneTime?: boolean;
  quantity?: number;
}

interface VariantRoomBoard {
  code: string;
  description: string;
  riep?: { totale: number; prezzo_medio: number; totale_ori: number };
}

interface VariantRoomRate {
  code: string;
  description: string;
  board: VariantRoomBoard[];
}

interface VariantRoom {
  code: string;
  description: string;
  guestNumber?: number;
  rate: VariantRoomRate[];
}

interface Variant {
  id: number;
  name: string;
  checkIn?: string;
  checkOut?: string;
  variantNote?: string;
  depositValue?: number;
  depositType?: string;
  option?: boolean;
  isDelete?: boolean;
  room: VariantRoom[];
  services?: VariantService[];
  paymentCodes?: string[];
  paymentMethods?: any[];
}

interface EstimateConfirmationData {
  id: number;
  variantId?: number;
  confirmedAt: string;
  name: string;
  surname: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  province?: string;
  cap?: string;
  country?: string;
  notes?: string;
  paymentMethodId?: number;
  paymentMethodCode?: string;
  paymentMethodDescription?: string;
  totalAmount: number;
  depositAmount?: number;
  pmsReservationCode?: string;
  transactionId?: string;
  paymentGateway?: string;
  pmsConfirmedAt?: string;
}

function parseDateString(s?: string | null): Date | null {
  if (!s) return null;
  const datePart = s.includes("T") ? s.split("T")[0] : s.split(" ")[0];
  const parts = datePart.split("-");
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
      return new Date(y, m, d);
    }
  }
  const parsed = new Date(s);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function variantServicesTotal(v: Variant): number {
  return (
    v.services?.reduce(
      (sum, s) => sum + (s.amount || 0) * (s.quantity || 1),
      0,
    ) || 0
  );
}

function variantGrossTotal(v: Variant): number {
  const roomsGross =
    v.room?.reduce((sum, r) => {
      return (
        sum +
        (r.rate?.reduce((s, rt) => {
          return (
            s +
            (rt.board?.reduce((b, bd) => {
              const ori = bd.riep?.totale_ori;
              const tot = bd.riep?.totale || 0;
              return b + (ori && ori > tot ? ori : tot);
            }, 0) || 0)
          );
        }, 0) || 0)
      );
    }, 0) || 0;

  return roomsGross + variantServicesTotal(v);
}

function variantTotal(v: Variant): number {
  const roomsTotal =
    v.room?.reduce(
      (sum, r) =>
        sum +
        (r.rate?.reduce(
          (s, rt) =>
            s +
            (rt.board?.reduce((b, bd) => b + (bd.riep?.totale || 0), 0) || 0),
          0,
        ) || 0),
      0,
    ) || 0;

  return roomsTotal + variantServicesTotal(v);
}

function variantDiscount(v: Variant): number {
  const gross = variantGrossTotal(v);
  const final = variantTotal(v);
  return gross > final ? gross - final : 0;
}

export default function PreventiveConfirmation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["checkout", "common"]);

  useDocumentTitle(t("confirmation.pageTitle") || "Dettaglio Preventivo Confermato");

  const [data, setData] = useState<any>(null);
  const [structureData, setStructureData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    axios
      .get(`/Estimate/preventive-detail/${token}`)
      .then((res) => {
        if (!res.data) {
          setIsExpired(true);
          setLoading(false);
          return;
        }

        // Se il preventivo non è ancora confermato, reindirizza alla pagina di selezione preventivo
        if (res.data.idPreventiveStatus !== 5) {
          navigate(`/preventive-detail/${token}`, { replace: true });
          return;
        }

        setData(res.data);
        const structureId = res.data.structureId;
        if (structureId) {
          axios
            .get(`/webcheckin/structure/${structureId}`)
            .then((sRes) => {
              setStructureData(sRes.data);
            })
            .catch(() => {})
            .finally(() => {
              setLoading(false);
            });
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        if (err.response?.status === 400 || err.response?.status === 404) {
          setIsExpired(true);
        } else {
          setIsError(true);
          setErrorMessage(
            err.response?.data?.message ||
              err.message ||
              "Errore di connessione.",
          );
        }
        setLoading(false);
      });
  }, [token, navigate]);

  const confirmation: EstimateConfirmationData | undefined = data?.confirmation;

  // Risoluzione della variante confermata:
  // 1. Dal confirmation.variantId
  // 2. Dalla variante con option == true
  // 3. Dalla prima variante disponibile
  const confirmedVariant: Variant | undefined = useMemo(() => {
    if (!data?.estimateVariants || data.estimateVariants.length === 0) return undefined;
    if (confirmation?.variantId) {
      const match = data.estimateVariants.find(
        (v: Variant) => v.id === confirmation.variantId,
      );
      if (match) return match;
    }
    const optioned = data.estimateVariants.find((v: Variant) => v.option && !v.isDelete);
    if (optioned) return optioned;
    return data.estimateVariants.find((v: Variant) => !v.isDelete) || data.estimateVariants[0];
  }, [data, confirmation]);

  // Calcolo delle date e delle notti
  const checkInDate = useMemo(() => {
    return parseDateString(confirmedVariant?.checkIn || data?.checkIn);
  }, [confirmedVariant, data]);

  const checkOutDate = useMemo(() => {
    return parseDateString(confirmedVariant?.checkOut || data?.checkOut);
  }, [confirmedVariant, data]);

  const nightsCount = useMemo(() => {
    if (!checkInDate || !checkOutDate) return 0;
    return Math.max(
      1,
      Math.round(
        (checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24),
      ),
    );
  }, [checkInDate, checkOutDate]);

  // Totali economici
  const totalAmount = useMemo(() => {
    if (confirmation?.totalAmount != null && confirmation.totalAmount > 0) {
      return Number(confirmation.totalAmount);
    }
    if (confirmedVariant) {
      return variantTotal(confirmedVariant);
    }
    return 0;
  }, [confirmation, confirmedVariant]);

  const depositAmount = useMemo(() => {
    if (confirmation?.depositAmount != null) {
      return Number(confirmation.depositAmount);
    }
    return 0;
  }, [confirmation]);

  const balanceAmount = useMemo(() => {
    const balance = totalAmount - depositAmount;
    return balance > 0 ? balance : 0;
  }, [totalAmount, depositAmount]);

  // Struttura e brand
  const structureCtx = structureData?.structureContextData;
  const structureName = structureCtx?.name || data?.structureName || "Hotel";
  const structureLogo = structureCtx?.logo || data?.structureLogo;
  const structureAddress =
    structureCtx?.address || data?.structureAddress || "";
  const structureCity = structureCtx?.city || data?.structureCity || "";
  const structureTel = structureCtx?.phone || data?.structureTel || "";
  const structureWebsite = structureCtx?.website || data?.structureWebsite || "";

  const mapSrc = useMemo(() => {
    if ((data?.structureAddress && data?.structureCity) || data?.googleMapsPosition) {
      return `https://maps.google.com/maps?q=${encodeURIComponent(
        data.googleMapsPosition ||
          structureCtx?.googleMapsPosition ||
          `${data.structureAddress}, ${data.structureCity}`,
      )}&z=15&output=embed`;
    }
    return null;
  }, [data, structureCtx]);

  const sanitizedArrivalNotes = useMemo(() => {
    return renderMarkdownAndSanitize(data?.minisite?.checkoutNotes);
  }, [data?.minisite?.checkoutNotes]);

  const sanitizedVariantNotes = useMemo(() => {
    return renderMarkdownAndSanitize(confirmedVariant?.variantNote);
  }, [confirmedVariant?.variantNote]);

  // Dati ospite confermati
  const guestName =
    confirmation?.name && confirmation?.surname
      ? `${confirmation.name} ${confirmation.surname}`
      : `${data?.name || ""} ${data?.surname || ""}`.trim();
  const guestEmail = confirmation?.email || data?.email || "—";
  const guestPhone = confirmation?.phone || data?.phone || "—";
  const guestAddress = confirmation?.address || "";
  const guestCity = confirmation?.city || "";
  const guestProvince = confirmation?.province || "";
  const guestCap = confirmation?.cap || "";
  const guestCountry = confirmation?.country || "";

  const fullBillingAddress = useMemo(() => {
    const parts = [
      guestAddress,
      guestCity ? `${guestCap ? guestCap + " " : ""}${guestCity}` : "",
      guestProvince ? `(${guestProvince})` : "",
      guestCountry,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  }, [guestAddress, guestCity, guestCap, guestProvince, guestCountry]);

  // Metodo di pagamento
  const paymentMethodLabel = useMemo(() => {
    if (confirmation?.paymentMethodDescription) {
      return confirmation.paymentMethodDescription;
    }
    const matched = data?.allowedPaymentMethods?.find(
      (m: any) => m.id === data?.idPaymentMethod,
    );
    if (matched?.descrizione) return matched.descrizione;
    return "Metodo di pagamento concordato";
  }, [confirmation, data]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
        <LoadingItem text="Caricamento dettagli preventivo confermato..." size="large" />
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-10 space-y-4">
            <Timer className="mx-auto h-16 w-16 text-amber-500" />
            <h2 className="text-2xl font-bold">
              {t("confirmation.notFoundTitle") || "Preventivo non trovato"}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t("confirmation.notFoundMessage") ||
                "Il preventivo richiesto non è più disponibile o il link è scaduto."}
            </p>
            <Button onClick={() => navigate("/")}>
              {t("backHome") || "Torna alla home"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-10 space-y-4">
            <AlertCircle className="mx-auto h-16 w-16 text-destructive" />
            <h2 className="text-2xl font-bold">Errore</h2>
            <p className="text-muted-foreground text-sm">{errorMessage}</p>
            <Button onClick={() => navigate("/")}>
              {t("backHome") || "Torna alla home"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background text-foreground py-6 px-4 sm:px-6 lg:px-8 print:p-0 print:bg-white print:text-black">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* TOP BAR / ACTIONS (HIDDEN IN PRINT) */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-2 border-b border-border/60 print:hidden">
            <div className="flex items-center gap-3">
              {structureLogo ? (
                <img
                  src={structureLogo}
                  alt={structureName}
                  className="h-10 w-auto object-contain max-w-[140px]"
                />
              ) : (
                <Building2 className="h-7 w-7 text-primary" />
              )}
              <div>
                <h2 className="text-base font-bold text-foreground">
                  {structureName}
                </h2>
                {(structureCity || structureAddress) && (
                  <p className="text-xs text-muted-foreground">
                    {[structureAddress, structureCity].filter(Boolean).join(", ")}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 self-end sm:self-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="gap-2 shadow-2xs font-medium text-xs"
              >
                <Printer className="h-4 w-4" />
                <span>{t("confirmation.print") || "Stampa / Salva PDF"}</span>
              </Button>
              <ModeToggle />
              <LanguageSelector />
            </div>
          </div>

          {/* CONFIRMATION HERO BANNER */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="overflow-hidden border-emerald-500/40 bg-gradient-to-br from-emerald-50/70 via-background to-background dark:from-emerald-950/20 dark:via-background dark:to-background shadow-sm print:border-none print:shadow-none">
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-emerald-600/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="h-7 w-7" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">
                          {t("confirmation.pageTitle") || "Preventivo Confermato"}
                        </h1>
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white font-semibold">
                          {t("confirmation.badgeConfirmed") || "Confermato"}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground text-sm max-w-2xl">
                        {t("confirmation.confirmedSubtitle") ||
                          "Il tuo soggiorno è stato confermato con successo. Di seguito trovi tutti i dettagli della prenotazione."}
                      </p>
                    </div>
                  </div>

                  {/* IDENTIFIER PILLS */}
                  <div className="flex flex-wrap md:flex-col items-start md:items-end gap-2 shrink-0 border-t md:border-t-0 pt-4 md:pt-0 border-border/60">
                    <div className="text-xs">
                      <span className="text-muted-foreground">
                        {t("confirmation.estimateNumber") || "Preventivo N°"}:{" "}
                      </span>
                      <span className="font-bold text-foreground">
                        #{data?.id}
                      </span>
                    </div>

                    {(confirmation?.pmsReservationCode || data?.hotelCode) && (
                      <div className="text-xs">
                        <span className="text-muted-foreground">
                          {t("confirmation.pmsCode") || "Codice Prenotazione"}:{" "}
                        </span>
                        <span className="font-bold text-emerald-600 dark:text-emerald-400 font-mono">
                          {confirmation?.pmsReservationCode || `#EST-${data?.id}`}
                        </span>
                      </div>
                    )}

                    <div className="text-xs text-muted-foreground">
                      <span>{t("confirmation.confirmedOn") || "Confermato il"}: </span>
                      <span className="font-medium text-foreground">
                        {confirmation?.confirmedAt
                          ? new Date(confirmation.confirmedAt).toLocaleDateString(
                              i18n.language === "en" ? "en-US" : "it-IT",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )
                          : new Date().toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* MAIN 2-COLUMN GRID */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* LEFT / MAIN COLUMN: STAY & VARIANT DETAILS (2 COLUMNS SPAN) */}
            <div className="lg:col-span-2 space-y-6">
              {/* STAY RECAP CARD */}
              <Card className="shadow-2xs">
                <CardContent className="p-6 space-y-6">
                  {/* Proposal Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-border/60">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-foreground">
                          {confirmedVariant?.name || "Proposta di Soggiorno"}
                        </h2>
                        <Badge variant="outline" className="border-emerald-500/40 text-emerald-600 dark:text-emerald-400">
                          Opzione confermata
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Riepilogo delle date e del trattamento prenotato
                      </p>
                    </div>

                    <div className="text-left sm:text-right">
                      <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">
                        {totalAmount.toFixed(2)} €
                      </div>
                      <span className="text-[11px] text-muted-foreground">
                        {t("confirmation.vatIncluded") || "IVA inclusa"}
                      </span>
                    </div>
                  </div>

                  {/* Stay Info Tiles */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="flex items-center gap-3 rounded-xl bg-muted/40 p-3.5 border border-border/50">
                      <div className="h-9 w-9 rounded-lg bg-background border flex items-center justify-center text-primary shrink-0">
                        <CalendarDays className="h-4.5 w-4.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] text-muted-foreground font-medium">
                          {t("confirmation.stayDetails") || "Soggiorno"} (
                          {nightsCount}{" "}
                          {nightsCount === 1
                            ? t("confirmation.night") || "notte"
                            : t("confirmation.nights") || "notti"}
                          )
                        </p>
                        <p className="text-xs font-semibold text-foreground truncate">
                          {checkInDate
                            ? checkInDate.toLocaleDateString(
                                i18n.language === "en" ? "en-US" : "it-IT",
                                { day: "numeric", month: "short", year: "numeric" },
                              )
                            : "—"}{" "}
                          -{" "}
                          {checkOutDate
                            ? checkOutDate.toLocaleDateString(
                                i18n.language === "en" ? "en-US" : "it-IT",
                                { day: "numeric", month: "short", year: "numeric" },
                              )
                            : "—"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 rounded-xl bg-muted/40 p-3.5 border border-border/50">
                      <div className="h-9 w-9 rounded-lg bg-background border flex items-center justify-center text-primary shrink-0">
                        <Users className="h-4.5 w-4.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] text-muted-foreground font-medium">
                          {t("confirmation.guests") || "Ospiti"}
                        </p>
                        <p className="text-xs font-semibold text-foreground truncate">
                          {data?.adultsNumber || 1}{" "}
                          {(data?.adultsNumber || 1) === 1
                            ? (t("confirmation.adult") || "adulto")
                            : (t("confirmation.adults") || "adulti")}
                          {data?.childrenNumber
                            ? `, ${data.childrenNumber} ${
                                data.childrenNumber === 1
                                  ? (t("confirmation.child") || "bambino")
                                  : (t("confirmation.children") || "bambini")
                              }`
                            : ""}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 rounded-xl bg-muted/40 p-3.5 border border-border/50">
                      <div className="h-9 w-9 rounded-lg bg-background border flex items-center justify-center text-primary shrink-0">
                        <Bed className="h-4.5 w-4.5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] text-muted-foreground font-medium">
                          {t("confirmation.roomsAndBoard") || "Camere"}
                        </p>
                        <p className="text-xs font-semibold text-foreground truncate">
                          {confirmedVariant?.room?.length || 1}{" "}
                          {(confirmedVariant?.room?.length || 1) === 1
                            ? "camera"
                            : "camere"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Confirmed Rooms & Treatments */}
                  <div className="space-y-3">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <Bed className="h-3.5 w-3.5" />
                      {t("confirmation.roomsAndBoard") || "Camere & Trattamento"}
                    </h3>

                    <div className="space-y-2.5">
                      {confirmedVariant?.room?.map((r, i) => {
                        const firstBoard = r.rate?.[0]?.board?.[0];
                        const promoDesc = firstBoard?.riep?.promo_desc;
                        const roomServices = (firstBoard?.riep as any)
                          ?.str_servizi as string | undefined;

                        return (
                          <div
                            key={i}
                            className="rounded-xl border border-border/70 bg-card p-4 space-y-2.5"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-bold text-foreground">
                                  {r.description || r.code || `Camera ${i + 1}`}
                                </span>
                                {firstBoard && (
                                  <span className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md text-xs font-semibold">
                                    <Utensils className="h-3 w-3 text-muted-foreground" />
                                    {firstBoard.description}
                                  </span>
                                )}
                                {r.rate?.[0]?.description && (
                                  <span className="text-xs text-muted-foreground">
                                    ({r.rate[0].description})
                                  </span>
                                )}
                                {promoDesc && (
                                  <span className="inline-flex items-center gap-1 bg-amber-100 dark:bg-amber-950/70 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800/80 px-2 py-0.5 rounded-md text-[11px] font-bold">
                                    <Tag className="h-3 w-3" />
                                    {promoDesc}
                                  </span>
                                )}
                              </div>

                              {firstBoard?.riep?.totale != null && (
                                <span className="text-sm font-bold text-foreground">
                                  {firstBoard.riep.totale.toFixed(2)} €
                                </span>
                              )}
                            </div>

                            {/* Room Services included */}
                            {roomServices && (
                              <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs text-cyan-800 dark:text-cyan-300">
                                <span className="font-semibold flex items-center gap-1 text-[11px]">
                                  <Gift className="h-3 w-3 text-cyan-600 dark:text-cyan-400" />
                                  Servizi inclusi nella camera:
                                </span>
                                {roomServices
                                  .split("|")
                                  .filter(Boolean)
                                  .map((sStr, sIdx) => {
                                    const p = sStr.split(";");
                                    return (
                                      <span
                                        key={sIdx}
                                        className="bg-cyan-50 dark:bg-cyan-950/60 border border-cyan-200 dark:border-cyan-800/80 px-2 py-0.5 rounded text-[11px] font-medium"
                                      >
                                        Cod. {p[0]} (×{p[1] || 1})
                                        {p[2] && parseFloat(p[2]) > 0
                                          ? ` - €${parseFloat(p[2]).toFixed(2)}`
                                          : ""}
                                      </span>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Included / Extra Package Services */}
                  {confirmedVariant?.services && confirmedVariant.services.length > 0 && (
                    <div className="rounded-xl border bg-emerald-50/50 dark:bg-emerald-950/20 p-4 space-y-2 border-emerald-200/80 dark:border-emerald-800/50">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 dark:text-emerald-300">
                        <Gift className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                        <span>
                          {t("confirmation.includedServices") ||
                            "Servizi inclusi ed extra nel pacchetto:"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {confirmedVariant.services.map((srv, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1.5 bg-background border border-emerald-300 dark:border-emerald-700/60 text-emerald-800 dark:text-emerald-300 text-xs px-2.5 py-1 rounded-lg font-medium"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                            {srv.description}
                            {srv.quantity && srv.quantity > 1 ? ` ×${srv.quantity}` : ""}
                            {srv.amount > 0
                              ? ` (${srv.amount.toFixed(2)} €)`
                              : " (Incluso)"}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Proposal Notes */}
                  {sanitizedVariantNotes && (
                    <div className="rounded-xl border border-amber-200/80 bg-amber-50/60 dark:border-amber-900/60 dark:bg-amber-950/20 p-4 space-y-2">
                      <div className="flex items-center gap-2 text-xs font-bold text-amber-900 dark:text-amber-200">
                        <StickyNote className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
                        <span>
                          {t("confirmation.proposalNotes") ||
                            "Note e dettagli della proposta"}
                        </span>
                      </div>
                      <div
                        className="prose prose-xs dark:prose-invert max-w-none text-xs text-amber-950/90 dark:text-amber-100/90 leading-relaxed break-words [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_ul]:my-1 [&_ol]:my-1"
                        dangerouslySetInnerHTML={{ __html: sanitizedVariantNotes }}
                      />
                    </div>
                  )}

                  {/* Price Breakdown Toggle & Table */}
                  {confirmedVariant && (
                    <div className="space-y-3 pt-2">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5" />
                          {t("confirmation.priceBreakdown") || "Riepilogo Costi"}
                        </h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
                          className="text-xs text-muted-foreground hover:text-foreground h-7 px-2 print:hidden"
                        >
                          {showPriceBreakdown ? (
                            <>
                              Nascondi <ChevronUp className="h-3 w-3 ml-1" />
                            </>
                          ) : (
                            <>
                              Dettagli <ChevronDown className="h-3 w-3 ml-1" />
                            </>
                          )}
                        </Button>
                      </div>

                      {showPriceBreakdown && (
                        <div className="border border-border/80 rounded-xl overflow-hidden shadow-2xs">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-muted/50 text-xs text-muted-foreground uppercase tracking-wider">
                                <th className="text-left p-3 font-semibold">
                                  {t("confirmation.roomService") || "Camera / Servizio"}
                                </th>
                                <th className="text-left p-3 font-semibold hidden sm:table-cell">
                                  {t("confirmation.treatment") || "Trattamento"}
                                </th>
                                <th className="text-right p-3 font-semibold">
                                  {t("confirmation.qty") || "Q.tà"}
                                </th>
                                <th className="text-right p-3 font-semibold">
                                  {t("confirmation.total") || "Totale"}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border/60">
                              {confirmedVariant.room?.map((r, ri) =>
                                r.rate?.map((rt, rti) =>
                                  rt.board?.map((bd, bi) => (
                                    <tr
                                      key={`${ri}-${rti}-${bi}`}
                                      className="hover:bg-muted/10 transition-colors"
                                    >
                                      <td className="p-3 font-medium text-foreground">
                                        {r.description || r.code}
                                        <div className="text-xs text-muted-foreground sm:hidden mt-0.5">
                                          {bd.description || rt.description || rt.code}
                                        </div>
                                      </td>
                                      <td className="p-3 text-muted-foreground text-xs hidden sm:table-cell">
                                        {bd.description || rt.description || rt.code}
                                      </td>
                                      <td className="p-3 text-right">1</td>
                                      <td className="p-3 text-right font-semibold text-foreground">
                                        {bd.riep?.totale?.toFixed(2) ?? "—"} €
                                      </td>
                                    </tr>
                                  )),
                                ),
                              )}
                              {confirmedVariant.services?.map((srv, sidx) => (
                                <tr
                                  key={`srv-${sidx}`}
                                  className="bg-emerald-50/20 dark:bg-emerald-950/10"
                                >
                                  <td className="p-3 font-medium text-emerald-800 dark:text-emerald-300">
                                    {srv.description}
                                  </td>
                                  <td className="p-3 text-muted-foreground text-xs hidden sm:table-cell">
                                    Servizio incluso/extra
                                  </td>
                                  <td className="p-3 text-right">
                                    {srv.quantity || 1}
                                  </td>
                                  <td className="p-3 text-right font-semibold text-emerald-700 dark:text-emerald-400">
                                    {(
                                      (srv.amount || 0) * (srv.quantity || 1)
                                    ).toFixed(2)}{" "}
                                    €
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="border-t divide-y divide-border/60 bg-muted/20 font-medium text-xs">
                              {variantDiscount(confirmedVariant) > 0 && (
                                <tr>
                                  <td colSpan={2} className="p-3 text-muted-foreground hidden sm:table-cell">
                                    {t("confirmation.discount") || "Sconto applicato"}
                                  </td>
                                  <td className="p-3 text-muted-foreground sm:hidden">
                                    {t("confirmation.discount") || "Sconto"}
                                  </td>
                                  <td className="p-3 text-right"></td>
                                  <td className="p-3 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                                    -{variantDiscount(confirmedVariant).toFixed(2)} €
                                  </td>
                                </tr>
                              )}
                              <tr className="text-sm font-bold bg-muted/40">
                                <td colSpan={2} className="p-3 text-foreground hidden sm:table-cell">
                                  {t("confirmation.grandTotal") || "Totale Complessivo"}
                                </td>
                                <td className="p-3 text-foreground sm:hidden">
                                  {t("confirmation.grandTotal") || "Totale"}
                                </td>
                                <td className="p-3 text-right"></td>
                                <td className="p-3 text-right text-base text-emerald-600 dark:text-emerald-400 font-black">
                                  {totalAmount.toFixed(2)} €
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* RIGHT COLUMN: GUEST, PAYMENT & HOTEL INFO (1 COLUMN SPAN) */}
            <div className="space-y-6">
              {/* GUEST & BILLING CARD */}
              <Card className="shadow-2xs">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-border/60">
                    <Users className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-bold text-foreground">
                      {t("confirmation.guestInfo") || "Dati Ospite & Fatturazione"}
                    </h3>
                  </div>

                  <div className="space-y-2.5 text-xs">
                    <div>
                      <span className="text-muted-foreground block text-[11px]">
                        {t("name") || "Intestatario"}
                      </span>
                      <span className="font-semibold text-foreground text-sm">
                        {guestName || "—"}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-2 pt-1">
                      <div className="flex items-center gap-2">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="text-foreground truncate">{guestEmail}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="text-foreground">{guestPhone}</span>
                      </div>
                    </div>

                    {fullBillingAddress && (
                      <div className="pt-2 border-t border-border/40">
                        <span className="text-muted-foreground block text-[11px] mb-1">
                          {t("confirmation.billingAddress") || "Indirizzo di fatturazione"}
                        </span>
                        <div className="flex items-start gap-2">
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                          <span className="text-foreground leading-relaxed">
                            {fullBillingAddress}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* PAYMENT & FINANCIAL RECAP CARD */}
              <Card className="shadow-2xs">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-border/60">
                    <CreditCard className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-bold text-foreground">
                      {t("confirmation.paymentDetails") || "Dettagli Pagamento"}
                    </h3>
                  </div>

                  <div className="space-y-3 text-xs">
                    <div className="p-3 rounded-lg bg-muted/40 border border-border/50 space-y-1">
                      <span className="text-muted-foreground block text-[11px]">
                        {t("paymentMethod") || "Modalità selezionata"}
                      </span>
                      <span className="font-bold text-foreground text-sm flex items-center gap-1.5">
                        <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                        {paymentMethodLabel}
                      </span>
                      {confirmation?.transactionId && (
                        <p className="text-[11px] text-muted-foreground font-mono mt-1">
                          {t("confirmation.transactionId") || "ID Transazione"}:{" "}
                          {confirmation.transactionId}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-center justify-between text-muted-foreground">
                        <span>{t("confirmation.grandTotal") || "Totale Soggiorno"}</span>
                        <span className="font-semibold text-foreground">
                          {totalAmount.toFixed(2)} €
                        </span>
                      </div>

                      {depositAmount > 0 && (
                        <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400 font-medium">
                          <span>
                            {t("confirmation.deposit") || "Caparra versata / richiesta"}
                          </span>
                          <span>{depositAmount.toFixed(2)} €</span>
                        </div>
                      )}

                      <Separator className="my-1.5" />

                      <div className="flex items-center justify-between font-bold text-sm text-foreground">
                        <span>{t("confirmation.balanceAtHotel") || "Saldo all'arrivo"}</span>
                        <span className="text-base text-foreground font-black">
                          {balanceAmount.toFixed(2)} €
                        </span>
                      </div>
                    </div>

                    {/* Bank transfer instructions if applicable */}
                    {paymentMethodLabel.toLowerCase().includes("bonifico") && (
                      <div className="p-3 rounded-lg bg-blue-50/70 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/40 text-[11px] text-blue-950 dark:text-blue-200 space-y-1 leading-relaxed">
                        <span className="font-bold block">
                          {t("bankTransferTitle") || "Coordinate per il bonifico"}
                        </span>
                        <p>
                          {data?.minisite?.paymentBankTransferText ||
                            t("bankTransferDefaultInstructions") ||
                            "Riceverai via email le coordinate bancarie (IBAN e causale) per effettuare il bonifico."}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* GUEST NOTES CARD (IF PRESENT) */}
              {(confirmation?.notes || data?.guestNote) && (
                <Card className="shadow-2xs">
                  <CardContent className="p-5 space-y-2">
                    <div className="flex items-center gap-2 pb-1 border-b border-border/60">
                      <StickyNote className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      <h3 className="text-sm font-bold text-foreground">
                        {t("confirmation.guestNotes") || "Note dell'Ospite"}
                      </h3>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line italic">
                      "{confirmation?.notes || data?.guestNote}"
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* HOTEL CONTACTS & LOCATION */}
              <Card className="shadow-2xs">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-border/60">
                    <Building2 className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-bold text-foreground">
                      {t("confirmation.hotelContacts") || "Contatti e Posizione"}
                    </h3>
                  </div>

                  <div className="space-y-2 text-xs">
                    <p className="font-bold text-foreground">{structureName}</p>
                    {structureAddress && (
                      <div className="flex items-start gap-2 text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <span>{[structureAddress, structureCity].filter(Boolean).join(", ")}</span>
                      </div>
                    )}
                    {structureTel && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        <a href={`tel:${structureTel}`} className="hover:underline text-foreground">
                          {structureTel}
                        </a>
                      </div>
                    )}
                    {structureWebsite && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Globe className="h-3.5 w-3.5 shrink-0" />
                        <a
                          href={structureWebsite.startsWith("http") ? structureWebsite : `https://${structureWebsite}`}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline text-foreground truncate"
                        >
                          {structureWebsite}
                        </a>
                      </div>
                    )}
                  </div>

                  {sanitizedArrivalNotes && (
                    <div className="p-3.5 rounded-lg bg-muted/40 border border-border/50 text-xs leading-relaxed space-y-1.5">
                      <span className="font-bold text-foreground block text-xs">
                        {t("confirmation.arrivalNotes") || "Note per l'arrivo"}:
                      </span>
                      <div
                        className="prose prose-xs dark:prose-invert max-w-none text-muted-foreground break-words text-xs leading-relaxed [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5"
                        dangerouslySetInnerHTML={{ __html: sanitizedArrivalNotes }}
                      />
                    </div>
                  )}

                  {mapSrc && (
                    <div className="rounded-lg overflow-hidden border border-border/60 h-40 mt-2 print:hidden">
                      <iframe
                        title="Mappa hotel"
                        src={mapSrc}
                        width="100%"
                        height="100%"
                        loading="lazy"
                        className="border-0"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}
